import { useCallback, useEffect, useRef, useState } from "react";

import { SafetyOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Flex, Input, Typography } from "antd";

import { sendTwoFACode, verifyTwoFACode } from "@neu/libui";
import type { TwoFAComponentProps } from "@neu/libui";

import styles from "./TwoFAVerify.module.scss";

const RESEND_COOLDOWN_SECONDS = 30;

export function TwoFAVerify({
	challengeToken,
	methods,
	onVerified,
	onCancel,
}: TwoFAComponentProps) {
	const [loading, setLoading] = useState(false);
	const [sending, setSending] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [cooldown, setCooldown] = useState(0);
	const [otpValue, setOtpValue] = useState("");
	const [codeSent, setCodeSent] = useState(false);
	const [activeChannel, setActiveChannel] = useState<"sms" | "email">(
		methods.find((m) => m.is_primary)?.channel ??
			methods[0]?.channel ??
			"sms"
	);
	const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const didBootstrapSendRef = useRef(false);

	const activeMethod =
		methods.find((m) => m.channel === activeChannel) ?? methods[0];
	const alternativeMethods = methods.filter(
		(m) => m.channel !== activeChannel
	);

	const startCooldown = useCallback(() => {
		setCooldown(RESEND_COOLDOWN_SECONDS);
		if (cooldownRef.current) clearInterval(cooldownRef.current);
		cooldownRef.current = setInterval(() => {
			setCooldown((prev) => {
				if (prev <= 1) {
					if (cooldownRef.current) clearInterval(cooldownRef.current);
					return 0;
				}
				return prev - 1;
			});
		}, 1000);
	}, []);

	useEffect(
		() => () => {
			if (cooldownRef.current) clearInterval(cooldownRef.current);
		},
		[]
	);

	const handleSendCode = useCallback(
		async (channel?: "sms" | "email") => {
			setSending(true);
			setError(null);
			try {
				await sendTwoFACode(challengeToken, channel);
				setCodeSent(true);
				startCooldown();
			} catch (err: unknown) {
				setError(
					err instanceof Error ? err.message : "Failed to send code"
				);
			} finally {
				setSending(false);
			}
		},
		[challengeToken, startCooldown]
	);

	useEffect(() => {
		didBootstrapSendRef.current = false;
	}, [challengeToken]);

	useEffect(() => {
		if (didBootstrapSendRef.current) return;
		didBootstrapSendRef.current = true;
		void handleSendCode(activeChannel);
	}, [activeChannel, challengeToken, handleSendCode]);

	const handleVerify = async (code: string) => {
		if (!code || code.length < 6) return;
		setLoading(true);
		setError(null);
		try {
			await verifyTwoFACode(challengeToken, code);
			onVerified();
		} catch (err: unknown) {
			setError(
				err instanceof Error ? err.message : "Verification failed"
			);
		} finally {
			setLoading(false);
		}
	};

	const handleSwitchChannel = (channel: "sms" | "email") => {
		setActiveChannel(channel);
		setError(null);
		setCodeSent(false);
		setOtpValue("");
		void handleSendCode(channel);
	};

	return (
		<Flex justify="center" align="center" className={styles.container}>
			<Card className={styles.card}>
				<Flex vertical align="center" className={styles.header}>
					<SafetyOutlined
						style={{
							fontSize: 40,
							color: "var(--color-primary-600)",
							marginBottom: 12,
						}}
					/>
					<Typography.Title level={4} style={{ margin: 0 }}>
						Two-Factor Verification
					</Typography.Title>
					{codeSent && activeMethod && (
						<Typography.Text
							type="secondary"
							style={{ marginTop: 8 }}
						>
							Code sent to {activeMethod.destination_masked}
						</Typography.Text>
					)}
				</Flex>

				{error && (
					<Alert
						message={error}
						type="error"
						showIcon
						closable
						onClose={() => {
							setError(null);
						}}
						style={{ marginBottom: 16, borderRadius: 8 }}
					/>
				)}

				<Flex vertical align="center" gap={16}>
					<Input.OTP
						className={styles.otp}
						length={6}
						value={otpValue}
						onChange={(value: string) => {
							setOtpValue(value);
							if (value.length === 6) {
								void handleVerify(value);
							}
						}}
						disabled={loading}
						size="large"
					/>

					<Button
						type="primary"
						size="large"
						loading={loading}
						disabled={!codeSent || otpValue.length < 6}
						block
						style={{ borderRadius: 12, height: 42 }}
						onClick={() => {
							void handleVerify(otpValue);
						}}
					>
						Verify Code
					</Button>
				</Flex>

				<Flex
					justify="space-between"
					align="center"
					className={styles.footer}
				>
					<Button
						type="link"
						size="small"
						disabled={cooldown > 0 || sending}
						loading={sending}
						onClick={() => {
							void handleSendCode(activeChannel);
						}}
					>
						{cooldown > 0
							? `Resend in ${cooldown}s`
							: "Resend code"}
					</Button>

					<Button type="link" size="small" onClick={onCancel}>
						Back to login
					</Button>
				</Flex>

				{alternativeMethods.length > 0 && (
					<Flex justify="center" style={{ marginTop: 8 }}>
						{alternativeMethods.map((m) => (
							<Button
								key={m.channel}
								type="link"
								size="small"
								onClick={() => {
									handleSwitchChannel(m.channel);
								}}
							>
								Try {m.channel === "email" ? "email" : "SMS"}{" "}
								instead
							</Button>
						))}
					</Flex>
				)}
			</Card>
		</Flex>
	);
}

export default TwoFAVerify;
